import { useCallback, useEffect, useState } from "react"
import { getAssessmentSession } from "~/api/assessmentSessions"
import type { AssessmentSessionResponse } from "~/api/assessmentSessions"
import type { DogPortraitInfo } from "~/api/auth"
import { heroPortraitShareUrl } from "~/utils/heroPortraitUrl"

type UseShareCardSessionArgs = {
  assessmentSessionToken?: string | null
  archetypeIcon?: string | null
  portrait?: DogPortraitInfo | null
  enabled?: boolean
}

export function useShareCardSession({
  assessmentSessionToken,
  archetypeIcon,
  portrait,
  enabled = true,
}: UseShareCardSessionArgs) {
  const [session, setSession] = useState<AssessmentSessionResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    if (!enabled || !assessmentSessionToken) {
      setSession(null)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)
    getAssessmentSession(assessmentSessionToken)
      .then((data) => {
        if (!cancelled) setSession(data)
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Could not load share card")
          setSession(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [assessmentSessionToken, enabled])

  const openShareModal = useCallback(() => {
    if (!session) return
    setModalOpen(true)
  }, [session])

  const closeShareModal = useCallback(() => {
    setModalOpen(false)
  }, [])

  const personalizedPortraitUrl = heroPortraitShareUrl(archetypeIcon, portrait)

  return {
    session,
    loading,
    error,
    modalOpen,
    openShareModal,
    closeShareModal,
    personalizedPortraitUrl,
    canShare: Boolean(session),
  }
}
